/**
 * PhoneInput Component
 *
 * Custom phone input with separate country selector and number input
 * Outputs phone numbers in E.164 format (e.g., +15551234567)
 *
 * @example
 * <PhoneInput
 *   value="+15551234567"
 *   onChange={setPhone}
 *   placeholder="555-123-4567"
 * />
 */

import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

export interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  error?: boolean;
  autoComplete?: string;
  name?: string;
  size?: 'default' | 'lg'; // default = h-10, lg = h-12
}

interface Country {
  code: string;
  name: string;
  flag: string;
  dialCode: string;
  format?: string;
}

const COUNTRIES: Country[] = [
  { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '+1', format: '(555) 555-5555' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', dialCode: '+1', format: '(555) 555-5555' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44', format: '7700 900000' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', dialCode: '+61', format: '412 345 678' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', dialCode: '+64', format: '21 123 4567' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', dialCode: '+49', format: '151 23456789' },
  { code: 'FR', name: 'France', flag: '🇫🇷', dialCode: '+33', format: '6 12 34 56 78' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', dialCode: '+34', format: '612 34 56 78' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', dialCode: '+39', format: '312 345 6789' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽', dialCode: '+52', format: '55 1234 5678' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷', dialCode: '+55', format: '11 91234-5678' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷', dialCode: '+54', format: '11 2345-6789' },
  { code: 'IN', name: 'India', flag: '🇮🇳', dialCode: '+91', format: '81234 56789' },
  { code: 'CN', name: 'China', flag: '🇨🇳', dialCode: '+86', format: '131 2345 6789' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', dialCode: '+81', format: '90-1234-5678' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', dialCode: '+82', format: '10-1234-5678' },
];

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value = '',
      onChange,
      placeholder,
      disabled = false,
      className,
      id,
      error = false,
      autoComplete = 'tel',
      name,
      size = 'default',
    },
    ref
  ) => {
    const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]);
    const [phoneNumber, setPhoneNumber] = useState('');

    // Parse incoming value (E.164 format or without + prefix) into country code and number
    useEffect(() => {
      console.log('[PhoneInput] useEffect triggered, value:', value);

      if (!value) {
        console.log('[PhoneInput] No value, clearing phone number');
        setPhoneNumber('');
        return;
      }

      // Normalize the value - add + if missing
      const normalizedValue = value.startsWith('+') ? value : `+${value}`;
      console.log('[PhoneInput] Normalized value:', normalizedValue);

      if (normalizedValue.startsWith('+')) {
        console.log('[PhoneInput] Attempting to parse');
        // Find matching country by dial code (try longest first for specificity)
        const sortedCountries = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length);
        const matchedCountry = sortedCountries.find(c => normalizedValue.startsWith(c.dialCode));
        console.log('[PhoneInput] Matched country:', matchedCountry);

        if (matchedCountry) {
          setSelectedCountry(matchedCountry);
          // Extract just the number part (remove country code)
          const numberPart = normalizedValue.substring(matchedCountry.dialCode.length);
          console.log('[PhoneInput] Number part:', numberPart);
          setPhoneNumber(numberPart);
        } else {
          console.log('[PhoneInput] No country matched, setting as-is');
          // If no country matches, just use the value as-is
          setPhoneNumber(value.replace(/\D/g, ''));
        }
      }
    }, [value]);

    const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const country = COUNTRIES.find(c => c.code === e.target.value);
      if (country) {
        setSelectedCountry(country);
        // Combine new country code with existing number
        const fullNumber = phoneNumber ? `${country.dialCode}${phoneNumber}` : '';
        onChange?.(fullNumber);
      }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Only allow digits
      const digits = e.target.value.replace(/\D/g, '');
      setPhoneNumber(digits);

      // Combine country code with number to create E.164 format
      const fullNumber = digits ? `${selectedCountry.dialCode}${digits}` : '';
      onChange?.(fullNumber);
    };

    // Format the display value based on country
    const getFormattedDisplayValue = (digits: string): string => {
      if (!digits) return '';

      // Format for +1 (US/Canada): (555) 123-4567
      if (selectedCountry.dialCode === '+1') {
        if (digits.length <= 3) {
          return `(${digits}`;
        } else if (digits.length <= 6) {
          return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        } else {
          return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
        }
      }

      // Format for +44 (UK): 1234 567890
      if (selectedCountry.dialCode === '+44') {
        if (digits.length <= 4) {
          return digits;
        } else {
          return `${digits.slice(0, 4)} ${digits.slice(4)}`;
        }
      }

      // Format for +61 (Australia): 412 345 678
      if (selectedCountry.dialCode === '+61') {
        if (digits.length <= 3) {
          return digits;
        } else if (digits.length <= 6) {
          return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        } else {
          return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
        }
      }

      // Default: no formatting
      return digits;
    };

    const heightClass = size === 'lg' ? 'h-12' : 'h-10';

    return (
      <div className={cn('flex gap-2 w-full', className)}>
        {/* Country Selector */}
        <select
          value={selectedCountry.code}
          onChange={handleCountryChange}
          disabled={disabled}
          className={cn(
            heightClass,
            'px-2 rounded-md border border-input !bg-white',
            'text-base md:text-sm font-medium !text-gray-900',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'transition-all duration-200',
            'w-[100px] shrink-0',
            error && 'border-red-400 focus-visible:ring-red-400/20'
          )}
        >
          {COUNTRIES.map((country) => (
            <option key={country.code} value={country.code}>
              {country.flag} {country.dialCode}
            </option>
          ))}
        </select>

        {/* Phone Number Input */}
        <input
          ref={ref}
          type="tel"
          id={id}
          name={name}
          value={getFormattedDisplayValue(phoneNumber)}
          onChange={handlePhoneChange}
          placeholder={placeholder || selectedCountry.format || 'Phone number'}
          disabled={disabled}
          autoComplete={autoComplete}
          className={cn(
            'flex-1 min-w-0',
            heightClass,
            'px-3 py-2 rounded-md border border-input !bg-white',
            'text-base md:text-sm !text-gray-900',
            'placeholder:text-gray-400',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'transition-all duration-200',
            error && 'border-red-400 focus-visible:ring-red-400/20'
          )}
        />

        {/* Hidden input with full E.164 value for form submission */}
        <input
          type="hidden"
          value={phoneNumber ? `${selectedCountry.dialCode}${phoneNumber}` : ''}
        />
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
